import { useTranslation } from 'react-i18next';

import type {
  FlightPlanCellResponse,
  TransportMatrixRowResponse,
  TransportSchemeTicketResponse,
} from '@/api/generated/schemas';
import { FlightCellStatus, LayerState } from '@/api/generated/schemas';
import type { RegularTransportMode } from '@/api/generated/schemas';
import { Icon } from '@/shared/ui/icon';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/utils';
import { formatDayShift, formatMinutes, formatTermLabel } from '../model/format';
import { SerieStateBadge } from '../../../detail/components/serieStateBadge';
import { TransportModeBadge } from '../../../../components/RegularTransportSelector';
import { DefinitionCell, InheritableCell } from './InheritableCell';
import {
  costAmountOf,
  definitionAmountOf,
  isGroupPricedTicket,
  isTotalTicket,
} from '../model/types';

export interface MatrixLegColumn {
  key: string;
  ticketId: string;
  facilityId: number;
  group: 'outbound' | 'inbound' | 'connected';
  label: string;
}

const LEG_GROUPS = ['outbound', 'inbound', 'connected'] as const;

/**
 * V jakém rozsahu matice stojí — rozhoduje, odkud se cena bere a kdo ji smí měnit.
 *
 * - `serie` — řádek na termín. Nahoře společné hodnoty seriálu, buňky je dědí a smí je ručně přepsat.
 *   Termín s vlastní definicí má jiné tickety, takže mu ceny sloučí do jedné informativní buňky.
 * - `termShared` — jediný termín, který dědí definici seriálu. Společné hodnoty se needitují (jsou
 *   seriálové), ale ruční přepis ceny pro tenhle termín ano.
 * - `termOwn` — jediný termín s vlastní definicí. Cena **je** cena jeho vlastního ticketu, takže se
 *   zadává napřímo a nemá z čeho dědit ani co přepisovat.
 */
export type MatrixMode = 'serie' | 'termShared' | 'termOwn';

interface TermFlightPlanMatrixProps {
  rows: TransportMatrixRowResponse[];
  /** Nákladové sloupce v pořadí, v jakém se mají zobrazit (řetěz, spojené lety, autobus). */
  tickets: TransportSchemeTicketResponse[];
  legColumns: MatrixLegColumn[];
  /** Popisek sloupce nákladu podle směrování ticketu, klíčovaný `ticketId`. */
  ticketLabels: Map<string, string>;
  /** Režim nákupu letenek per ticket — badge vedle směrování. Autobusová strana ho nemá. */
  ticketModes: Map<string, RegularTransportMode>;
  cells: Map<string, FlightPlanCellResponse>;
  currency: string | null;
  /** Nabídka měn včetně prázdné volby. */
  currencyOptions: string[];
  sharedCapacity: number | null;
  /** Rozsah matice — viz {@link MatrixMode}. */
  mode: MatrixMode;
  readOnly: boolean;
  isFlightPlanLoading: boolean;
  onOpenTerm: (termId: string) => void;
  onCurrencyChange: (currency: string | null) => void;
  onSharedCapacityChange: (value: number | null) => void;
  /** Cena na ticketu, tedy v definici schématu — společná hodnota seriálu i cena vlastní definice. */
  onDefinitionCostChange: (ticketId: string, value: number | null) => void;
  onCostChange: (termId: string, ticketId: string, value: number | null, isManual: boolean) => void;
  onCapacityChange: (termId: string, value: number | null, isManual: boolean) => void;
}

/**
 * Matice „Letový plán termínů" — řádek = termín, sloupce = kapacita, náklad per nákupní jednotka
 * a skutečné časy jednotlivých legů.
 *
 * Geometrie (tříúrovňové sticky záhlaví nad sticky prvním sloupcem, hranice skupin) je v
 * `transportScheme.css`; Tailwind ji vyjádřit neumí, protože sticky offsety musí odpovídat skutečným
 * výškám řádků záhlaví.
 *
 * Časy dorazí až druhým dotazem (`flight-plan`), do té doby mají buňky skeleton — panely směrů už
 * mezitím stojí kompletní.
 */
export function TermFlightPlanMatrix({
  rows,
  tickets,
  legColumns,
  ticketLabels,
  ticketModes,
  cells,
  currency,
  currencyOptions,
  sharedCapacity,
  mode,
  readOnly,
  isFlightPlanLoading,
  onOpenTerm,
  onCurrencyChange,
  onSharedCapacityChange,
  onDefinitionCostChange,
  onCostChange,
  onCapacityChange,
}: TermFlightPlanMatrixProps) {
  const { t } = useTranslation('venua-planner');

  const showSharedValues = mode === 'serie';
  // Vlastní definice termínu: cena je přímo na jeho ticketu, dědit ani přepisovat není z čeho.
  const costsAreDefinition = mode === 'termOwn';

  // Spojený let sloupec nákladu má, ale do součtu „Celkem" nevstupuje — je to příplatek.
  const spannedColumns = tickets.length + 1 + legColumns.length;

  if (rows.length === 0) {
    return (
      <p className="text-phx-xs text-phx-text-muted px-3 py-6 text-center">
        {t('transportScheme.matrix.empty')}
      </p>
    );
  }

  return (
    <div className="op-matrix">
      <table>
        <thead>
          <tr>
            <th className="op-stick" rowSpan={2}>
              {t('transportScheme.matrix.term')}
            </th>
            <th data-grp>{t('transportScheme.matrix.state')}</th>
            <th data-grp>{t('transportScheme.matrix.capacity')}</th>
            <th data-grp colSpan={tickets.length + 1}>
              {t('transportScheme.matrix.cost')}
              {/* Měna je vlastnost celého schématu — v detailu termínu se jen ukazuje. */}
              {showSharedValues ? (
                <select
                  className="op-cursel ml-1 font-mono"
                  value={currency ?? ''}
                  disabled={readOnly}
                  onChange={(event) => onCurrencyChange(event.target.value || null)}
                >
                  <option value="">—</option>
                  {currencyOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              ) : currency ? (
                <span className="ml-1 font-mono">{currency}</span>
              ) : null}
            </th>
            {LEG_GROUPS.map((group) => {
              const groupColumns = legColumns.filter((column) => column.group === group);
              if (groupColumns.length === 0) return null;
              return (
                <th key={group} data-grp colSpan={groupColumns.length}>
                  {t(`transportScheme.matrix.group.${group}`)}
                </th>
              );
            })}
            {/* Umělý sloupec na konci spolkne volné místo, aby se tabulka na širokém monitoru
                neroztahovala do nesmyslných šířek. */}
            <th className="fillc" rowSpan={showSharedValues ? 3 : 2} />
          </tr>

          <tr>
            <th className="stc op-bd">{t('transportScheme.matrix.stateSub')}</th>
            <th className="capc op-bd">{t('transportScheme.matrix.capacitySub')}</th>
            {tickets.map((ticket, index) => {
              const ticketMode = ticketModes.get(ticket.ticketId);
              return (
                <th key={ticket.ticketId} className={cn('cst', index === 0 && 'op-bd')}>
                  <span className="op-colh">
                    <span className="code">{ticketLabels.get(ticket.ticketId) ?? '—'}</span>
                    {ticketMode ? <TransportModeBadge mode={ticketMode} t={t} /> : null}
                  </span>
                </th>
              );
            })}
            <th className="totc">{t('transportScheme.matrix.total')}</th>
            {legColumns.map((column, index) => (
              <th
                key={column.key}
                className={cn('legc', isGroupStart(legColumns, index) && 'op-bd')}
              >
                <span className="code">{column.label}</span>
              </th>
            ))}
            <th className="fillc" />
          </tr>

          {/* Společné hodnoty — zdroj pro všechny zděděné buňky pod nimi. Patří seriálu, takže
              v detailu termínu se řádek vůbec nekreslí; termín má vlastní buňky na svém řádku. */}
          {showSharedValues ? (
            <tr className="op-shrow">
              <th className="op-stick shl">
                <Icon name="share" className="h-3 w-3" />
                {t('transportScheme.matrix.shared')}
              </th>
              <th />
              <th className="capc">
                <DefinitionCell
                  value={sharedCapacity}
                  readOnly={readOnly}
                  title={t('transportScheme.matrix.sharedCapacityHint')}
                  onChange={onSharedCapacityChange}
                />
              </th>
              {tickets.map((ticket, index) => (
                <th key={ticket.ticketId} className={cn('cst', index === 0 && 'op-bd')}>
                  <DefinitionCell
                    value={definitionAmountOf(ticket)}
                    unit={unitOf(ticket)}
                    readOnly={readOnly}
                    title={t(
                      isGroupPricedTicket(ticket)
                        ? 'transportScheme.matrix.sharedGroupCostHint'
                        : 'transportScheme.matrix.sharedCostHint'
                    )}
                    onChange={(value) => onDefinitionCostChange(ticket.ticketId, value)}
                  />
                </th>
              ))}
              <th className="totc">
                {sharedTotal(tickets)}
                {currency ? <span className="cur">{currency}</span> : null}
              </th>
              {legColumns.map((column, index) => (
                <th key={column.key} className={cn(isGroupStart(legColumns, index) && 'op-bd')} />
              ))}
            </tr>
          ) : null}
        </thead>

        <tbody>
          {rows.map((row) => {
            const isDraft = row.termLayerState === LayerState.Draft;
            const label = formatTermLabel(row.dateFrom, row.nights);
            // U vlastní definice se sčítá přímo z ticketů — řádkové buňky tam žádnou vlastní
            // hodnotu nenesou, jen zrcadlí definici.
            const total = costsAreDefinition
              ? sharedTotal(tickets)
              : tickets.filter(isTotalTicket).reduce(
                  (sum, ticket) =>
                    sum +
                    (costAmountOf(
                      row.costs.find((cost) => cost.ticketId === ticket.ticketId),
                      ticket
                    ) ?? 0),
                  0
                );

            return (
              <tr key={row.termId} {...(row.isOwnTransport ? { 'data-own': '' } : {})}>
                <td className="op-stick">
                  <span className="flex items-center gap-1">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      iconLeft="external-link"
                      title={t('transportScheme.matrix.openTerm')}
                      onClick={() => onOpenTerm(row.termId)}
                    />
                    <span className="tlabel">
                      {label.start} <span className="tl-w">({label.startWeekday})</span> -{' '}
                      {label.end}{' '}
                      <span className="tl-n">
                        ({t('transportScheme.matrix.nights', { count: label.nights })})
                      </span>
                    </span>
                  </span>
                </td>

                {/* Sloupec ukazuje stav DOPRAVNÍ vrstvy; `termLayerState` níž jen řídí, jestli už má
                    smysl termín ocenit. */}
                <td className="stc">
                  <SerieStateBadge state={row.operationLayerState} />
                </td>

                <td className="capc">
                  <InheritableCell
                    value={row.capacity ?? null}
                    isManual={row.isOwnCapacity}
                    readOnly={readOnly}
                    onChange={(value, isManual) => onCapacityChange(row.termId, value, isManual)}
                  />
                </td>

                {/* Slučená buňka jen v seriálu: tam má termín s vlastní definicí jiné tickety, než
                    na jaké stojí sloupce, takže mu ceny ukázat nejde. Ve svém detailu ale sloupce
                    JEHO definici odpovídají — tam se ceny normálně zadávají. */}
                {row.isOwnTransport && mode === 'serie' ? (
                  <td className="own op-bd" colSpan={spannedColumns}>
                    <span className="op-own">
                      <Icon name="cog" className="h-3 w-3" />
                      {t('transportScheme.matrix.ownTransport')}
                    </span>
                    <span className="op-own-note">
                      {t('transportScheme.matrix.ownTransportHint')}
                    </span>
                  </td>
                ) : (
                  <>
                    {tickets.map((ticket, index) => {
                      const cost = row.costs.find((c) => c.ticketId === ticket.ticketId);
                      return (
                        <td key={ticket.ticketId} className={cn('cst', index === 0 && 'op-bd')}>
                          {isDraft ? (
                            <span
                              className="op-nocost"
                              title={t('transportScheme.matrix.draftHint')}
                            >
                              —
                            </span>
                          ) : costsAreDefinition ? (
                            <DefinitionCell
                              value={definitionAmountOf(ticket)}
                              unit={unitOf(ticket)}
                              readOnly={readOnly}
                              title={t('transportScheme.matrix.ownCostHint')}
                              onChange={(value) => onDefinitionCostChange(ticket.ticketId, value)}
                            />
                          ) : (
                            <InheritableCell
                              value={costAmountOf(cost, ticket)}
                              isManual={cost?.isManual ?? false}
                              readOnly={readOnly}
                              onChange={(value, isManual) =>
                                onCostChange(row.termId, ticket.ticketId, value, isManual)
                              }
                            />
                          )}
                        </td>
                      );
                    })}

                    <td className="totc">
                      {isDraft ? '—' : total}
                      {!isDraft && currency ? <span className="cur">{currency}</span> : null}
                    </td>

                    {legColumns.map((column, index) => (
                      <td
                        key={column.key}
                        className={cn('legc', isGroupStart(legColumns, index) && 'op-bd')}
                      >
                        <FlightCell
                          cell={cells.get(`${column.ticketId}:${column.facilityId}:${row.termId}`)}
                          isLoading={isFlightPlanLoading}
                        />
                      </td>
                    ))}
                  </>
                )}

                <td className="fillc" />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Legenda matice — patří do patičky panelu, ne do těla, aby tabulka lícovala s jeho okrajem. */
export function TermFlightPlanMatrixLegend() {
  const { t } = useTranslation('venua-planner');

  return (
    <span className="op-matrix-legend text-phx-2xs">
      <span className="it">
        <Icon name="share" className="h-3 w-3" />
        {t('transportScheme.matrix.legend.shared')}
      </span>
      <span className="it" data-manual>
        <Icon name="tune" className="h-3 w-3" />
        {t('transportScheme.matrix.legend.manual')}
      </span>
      <span className="it">{t('transportScheme.matrix.legend.draft')}</span>
      <span className="it">{t('transportScheme.matrix.legend.flights')}</span>
    </span>
  );
}

/** Buňka skutečného spoje — chybí let / let bez času / časy. */
function FlightCell({ cell, isLoading }: { cell?: FlightPlanCellResponse; isLoading: boolean }) {
  const { t } = useTranslation('venua-planner');

  if (isLoading || !cell) {
    return <span className="bg-phx-surface-3 rounded-phx-xs inline-block h-3 w-12 animate-pulse" />;
  }

  if (cell.status === FlightCellStatus.Missing) {
    return <span className="op-miss">{t('transportScheme.matrix.missingFlight')}</span>;
  }

  if (cell.status === FlightCellStatus.WithoutTime) {
    return (
      <span className="op-miss" data-notime>
        {t('transportScheme.matrix.flightWithoutTime')}
      </span>
    );
  }

  const arrivalShift = formatDayShift(cell.arrivalDayShift);
  return (
    <span>
      {cell.departureMinutes != null ? formatMinutes(cell.departureMinutes) : '—'}
      {' – '}
      {cell.arrivalMinutes != null ? formatMinutes(cell.arrivalMinutes) : '—'}
      {arrivalShift ? <span className="text-phx-text-muted ml-0.5">{arrivalShift}</span> : null}
    </span>
  );
}

/** První sloupec skupiny dostane svislou hranici — tím se skupiny opticky oddělí. */
function isGroupStart(columns: MatrixLegColumn[], index: number): boolean {
  return index === 0 || columns[index - 1].group !== columns[index].group;
}

function sharedTotal(tickets: TransportSchemeTicketResponse[]): number {
  return tickets
    .filter(isTotalTicket)
    .reduce((sum, ticket) => sum + (definitionAmountOf(ticket) ?? 0), 0);
}

/**
 * Jednotka ceny za vstupem — bez ní by u autobusu nebylo poznat, že jde o částku za celou skupinu,
 * a ne za osobu jako u letenek.
 */
function unitOf(ticket: TransportSchemeTicketResponse): 'pax' | 'grp' {
  return isGroupPricedTicket(ticket) ? 'grp' : 'pax';
}
